import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import { 
  History, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  ChevronDown, 
  ChevronUp,
  Trash2,
  Loader2,
  Clock
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface HistoryItem {
  id: string;
  input_text: string | null;
  input_type: string;
  ampel_result: 'gruen' | 'gelb' | 'rot';
  headline: string | null;
  explanation: string | null;
  aktionen: string[] | null;
  created_at: string;
}

interface CheckHistoryProps {
  userId: string;
  isVisible: boolean;
}

const ampelConfig = {
  gruen: {
    icon: CheckCircle,
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    label: 'Sicher'
  },
  gelb: {
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    label: 'Verdächtig'
  },
  rot: {
    icon: Shield,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    label: 'Gefährlich'
  }
};

export function CheckHistory({ userId, isVisible }: CheckHistoryProps) {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchHistory = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('klartext_check_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible && userId) {
      fetchHistory();
    }
  }, [isVisible, userId]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('klartext_check_history')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setHistory(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting history item:', error);
    } finally {
      setDeletingId(null);
    }
  };

  if (!isVisible) return null;

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <History className="h-5 w-5" />
          Ihre Check-Historie
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <History className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>Noch keine Checks durchgeführt.</p>
            <p className="text-sm">Ihre Analysen werden hier gespeichert.</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {history.map((item) => {
                const config = ampelConfig[item.ampel_result];
                const IconComponent = config.icon;
                const isExpanded = expandedId === item.id;

                return (
                  <div
                    key={item.id}
                    className={`rounded-lg border ${config.border} ${config.bg} p-4 transition-all`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-full ${config.bg}`}>
                          <IconComponent className={`h-5 w-5 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-sm font-semibold ${config.color}`}>
                              {config.label}
                            </span>
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(new Date(item.created_at), "dd. MMM yyyy, HH:mm", { locale: de })}
                            </span>
                          </div>
                          <h4 className="font-medium text-slate-900 truncate">
                            {item.headline || 'Analyse'}
                          </h4>
                          {item.input_text && (
                            <p className="text-sm text-slate-600 truncate mt-1">
                              {item.input_text.substring(0, 100)}
                              {item.input_text.length > 100 ? '...' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedId(isExpanded ? null : item.id)}
                          className="h-8 w-8 p-0"
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          disabled={deletingId === item.id}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                        >
                          {deletingId === item.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        {item.explanation && (
                          <div className="mb-3">
                            <h5 className="text-sm font-semibold text-slate-700 mb-1">Erklärung:</h5>
                            <p className="text-sm text-slate-600">{item.explanation}</p>
                          </div>
                        )}
                        {item.aktionen && item.aktionen.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-slate-700 mb-1">Empfehlungen:</h5>
                            <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                              {item.aktionen.map((aktion, i) => (
                                <li key={i}>{aktion}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {item.input_text && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <h5 className="text-sm font-semibold text-slate-700 mb-1">Ursprünglicher Text:</h5>
                            <p className="text-xs text-slate-500 bg-white/50 p-2 rounded whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {item.input_text}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
